import Promise from 'bluebird';
import bootbox from 'bootbox';
import _ from 'lodash';

import api from '../api';
import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import translate from '../core/tools/translate';
import templates from '../core/templates';
import environment from '../environment';
import { customScrollbar, isLayerEnabled } from '../shared';
import { FeaturePermission } from '../../constants/feature.permission.helper';
import { setExportActionCss } from '../../utils';
import { selectMarker } from '../shared/map';
import { CONFIG } from '../../config';
import { PointOfInterestSpreadsheet } from '../../spreadsheets/point-of-interest-spreadsheet';

const LayerControls = (name) => require('../controls').get(name);
const LayerInfo = (name) => require('../info').get(name);

const currentLayer = () => LayerControls('PointOfInterest').getLayer();
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

const name = 'PointOfInterest';

const _getContainer = () =>
    infoBoard.createContainer({
        id: 'points',
        name: translate('Common.PointOfInterest')
    });

const _getImage = latlng =>
    new Promise((resolve) => {
        const images = {
            original: '#',
            thumbnail: `https://maps.googleapis.com/maps/api/streetview?size=${worldMap.getThumbImageSize()}&location=${latlng.lat},${latlng.lng}&sensor=false&key=${CONFIG.GOOGLE_MAPS_API_KEY}`
        };
        return resolve(images);
    });

const _getInfo = (feature, resultType) =>
    new Promise((resolve) =>
        api.poi.getPoi(feature.properties.Id, resultType).then(response => resolve(response))
    );

const _getContext = function () {
    return _.values(environment.get('CONFIG.FILTERS.POINTS_TYPES'))[0];
};

class PointOfInterestInfo {
    static getInstance() {
        if (!PointOfInterestInfo._instance) {
            PointOfInterestInfo._instance = new PointOfInterestInfo();
        }

        return PointOfInterestInfo._instance;
    }

    init() {
    }

    _getFeatureLayer(feature) {
        return currentLayer().getLayer(feature.properties.Id);
    }

    _updateVoronoi(feature, layer) {
        if (worldMap.hasLayer(layer)) {
            return LayerControls('Voronoi').add([feature]);
        } else {
            return LayerControls('Voronoi').remove([feature.properties.Id]);
        }
    }

    create(features) {
        if (infoBoard.locked) {
            return;
        }

        const container = _getContainer();

        if (infoBoard.dataUnavailable({
            features,
            container,
            isEnabled: isLayerEnabled(name)
        })) {
            return;
        }

        const context = _getContext();

        const _entry = {};

        const template = $(templates('infoboard-content', {
            id: 'points-collapse',
            actions: isAuthorized(FeaturePermission.MAP_POINT_INTEREST.exportPoints)
        }));
        const content = template.find('.panel-group');

        features.forEach(feature => {
            let item;
            let layer = this._getFeatureLayer(feature);

            const recalculeteArea = () => {
                LayerInfo('Summary').recalculate({
                    container: item.summary,
                    id: feature.properties.Id,
                    latlng: layer.getLatLng(),
                    value: feature.properties['RaioServiceAreaMt'],
                    layer: currentLayer(),
                    controls: LayerControls('PointOfInterest').getControls(),
                    update: false,
                    feature
                });
                $(item.btn.summary).tab('show');
            };

            let options = {
                feature,
                name: name.toLocaleLowerCase(),
                originalName: name,
                container,
                controls: {
                    info: true,
                    hideStore: true,
                    editPointOfInterest: isAuthorized(FeaturePermission.MAP_POINT_INTEREST.editOption) && feature.properties.CanEdit,
                    delPointOfInterest: isAuthorized(FeaturePermission.MAP_POINT_INTEREST.deleteOption) && feature.properties.CanEdit,
                },
            };

            item = infoBoard.createItem(options);
            if (!item) {
                return;
            }

            _entry[feature.properties.Id] = feature;


            if (item.btn.editPointOfInterest) {
                item.btn.editPointOfInterest.on('click', () =>
                    LayerInfo('CreatePoint').edit(currentLayer(), feature, context)
                );
            }

            if (item.btn.delPointOfInterest) {
                item.btn.delPointOfInterest.on('click', () => {
                    return bootbox.dialog({
                        title: `<i class="fa fa-warning"></i> ${translate('Common.Warning')}`,
                        message: translate('Validation.RemovePoint'),
                        backdrop: false,
                        buttons: {
                            main: {
                                label: `<i class="fa fa-times"></i> ${translate('Action.Cancel')}`,
                                className: 'btn-default'
                            },
                            danger: {
                                label: `<i class="fa fa-trash-o"></i> ${translate('Action.Remove')}`,
                                className: 'btn-danger',
                                callback() {
                                    api.poi.remove(feature.properties.Id);
                                    currentLayer().removeLayer(feature.properties.Id);
                                }
                            }
                        }
                    });
                });
            }

            if (item.btn.info) {
                item.btn.info.on('click', e => {
                    e.preventDefault();
                    const queue = [];
                    const coordinates = {
                        lat: feature.geometry.coordinates[1],
                        lng: feature.geometry.coordinates[0]
                    };
                    queue.push(_getInfo(feature, context));
                    queue.push(_getImage(coordinates));
                    Promise.all(queue).then(response => {
                        const data = response[0];
                        data.image = response[1];
                        data.name = feature.properties['Name'].toCapitalCase();
                        data.id = feature.properties.Id;
                        LayerInfo('Resume').show(data, {
                            resultType: 'Points',
                            visible: true,
                            latlng: layer.getLatLng(),
                            title: translate('Common.PointInfo'),
                            callbackBack: () => {
                                infoBoard.showInfo('Points', feature.properties.Id);
                                worldMap.getCurrent().fireEvent('click');
                            },
                        });
                    });

                    selectMarker(layer, feature, 'PointOfInterest', 'points');
                });
            }

            if (item.btn.summary) {
                item.btn.summary.on('click', function (e) {
                    e.preventDefault();
                    if (feature.properties.PossuiServiceArea) {
                        options = {
                            // container: item.summary
                            id: feature.properties.Id,
                            latlng: layer.getLatLng(),
                            type: 7,
                            context: 'points',
                            layer: currentLayer(),
                            title: feature.properties.Nome,
                            value: feature.properties.RaioServiceAreaMt,
                            visible: true
                        };
                        // scrollTo: true
                        LayerInfo('Summary').create(options);
                    } else if (isAuthorized('points-service-area-generate')) {
                        recalculeteArea();
                    }
                });
            }

            if (item.btn.hideStore) {
                // toggle icon if the current feature was target as hidden
                if (currentLayer('PointOfInterest').isHidden(feature.properties.Id)) {
                    $(item.btn.hideStore).parent().find('i').toggleClass('fa-eye-slash');
                    $(item.btn.hideStore).parent().find('a').text(translate('Action.ShowStore'));
                    item.title.toggleClass('disabled');
                    this._updateVoronoi(feature);
                }

                if (!currentLayer('PointOfInterest').isHidden(feature.properties.Id)) {
                    $(item.btn.hide).parent().find('i').toggleClass('fa-eye');
                    $(item.btn.hide).parent().find('a').text(translate('Action.HideStore'));
                }

                item.btn.hideStore.on('click', () => {
                    LayerControls('PointOfInterest').toggleVisibility(feature.properties.Id, layer);
                    if (!layer) {
                        layer = this._getFeatureLayer('PointOfInterest', feature);
                    }

                    return this._updateVoronoi(feature, layer);
                });
            }

            $(content).append(item.layout);
        });

        $(container).detach().append(template).appendTo('#infoboard-content');

        const actions = infoBoard.createActions({
            export: {
                value: isAuthorized(FeaturePermission.MAP_POINT_INTEREST.exportPoints),
                label: translate('Action.ExportPoints')
            },
            compare: {
                value: false
            }
        }, 'PointOfInterest');
        actions.layout.appendTo(container);

        if (actions.export) {
            setExportActionCss();
            $(actions.export).on('click', e => {
                e.stopPropagation();
                (new PointOfInterestSpreadsheet()).mount(features).create().saveAsExcel();
            });
        }

        customScrollbar(content);
    }
}

export default PointOfInterestInfo;



// WEBPACK FOOTER //
// ./src/scripts/info/PointOfInterestInfo.js